import { createRoot, createSignal } from "solid-js";
import { SUPPORTED_LOCALES, type Locale } from "@energyplatform/i18n";
import type { UserPreferences } from "@energyplatform/client";
import { locale, setLocale } from "./i18n.ts";
import { api, session } from "./services.ts";

// The signed-in person's settings (host: /me/preferences). They follow the person to every device; until they are
// loaded, or when signed out, the browser's language and theme and the view profile's density apply.

export const DEFAULT_PREFERENCES: UserPreferences = {
  language: null,
  theme: "system",
  density: null,
  mutedPriorities: [],
};

const state = createRoot(() => {
  const [prefs, setPrefs] = createSignal<UserPreferences>(DEFAULT_PREFERENCES);
  return { prefs, setPrefs };
});

/** Current preferences (reactive). */
export const preferences = state.prefs;

/** Density chosen in the profile, or null to follow the view profile. */
export const densityOverride = () => state.prefs().density;

/** Puts the theme on the document: an explicit light or dark one, or none to follow the system. */
export function applyTheme(theme: string): void {
  if (theme === "light" || theme === "dark") {
    document.documentElement.dataset.theme = theme;
  } else {
    delete document.documentElement.dataset.theme;
  }
}

/** Reads the person's settings from the host and applies them; fails when they cannot be read. */
export async function reloadPreferences(): Promise<UserPreferences> {
  const p = await api.preferences.get();
  state.setPrefs(p);
  applyTheme(p.theme);
  if (
    p.language &&
    p.language !== locale() &&
    (SUPPORTED_LOCALES as readonly string[]).includes(p.language)
  ) {
    await setLocale(p.language as Locale);
  }
  return p;
}

/** Loads the person's settings after sign-in; the defaults stay when the request fails. */
export async function loadPreferences(): Promise<void> {
  if (session().status !== "signedIn") {
    return;
  }
  try {
    await reloadPreferences();
  } catch {
    // The application works with the defaults; the profile page reads them again and shows the error.
  }
}

/** Saves a change and applies it at once; the saved settings come back normalised. */
export async function savePreferences(
  change: Partial<UserPreferences>,
): Promise<UserPreferences> {
  const saved = await api.preferences.save({ ...state.prefs(), ...change });
  state.setPrefs(saved);
  applyTheme(saved.theme);
  if (saved.language && saved.language !== locale()) {
    await setLocale(saved.language as Locale);
  }
  return saved;
}

/**
 * The language switch of the shell: changes the language now and, for a signed-in person who chose a language in the
 * profile, keeps the profile in step so the next sign-in does not switch it back.
 */
export async function chooseLanguage(next: Locale): Promise<void> {
  await setLocale(next);
  if (session().status === "signedIn" && state.prefs().language !== null) {
    try {
      await savePreferences({ language: next });
    } catch {
      // The language still changed for this session.
    }
  }
}
